import React, { useState, useEffect } from "react";
import Highcharts from "highcharts";
import HighchartsReact from "highcharts-react-official";
import PatternFill from "highcharts/modules/pattern-fill";
import { useSelector } from "react-redux";
import { getVesselEmissionState } from "../../../services/vesselEmissionService";
import { ChevronDown, SlidersHorizontal } from "lucide-react";

// Initialize the pattern fill module
if (typeof PatternFill === "function") {
  PatternFill(Highcharts);
} else if (PatternFill.default && typeof PatternFill.default === "function") {
  PatternFill.default(Highcharts);
}

const VesselStateTransition = ({ voyageId }) => {
  const { startDate, endDate } = useSelector((state) => state.dateRange);
  const { currentVessel } = useSelector((state) => state.vesselOverview);
  const [vesselState, setVesselState] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const colors = [
    "#1a5276", // (CO₂)
    "#27ae60", // (SOx)
    "#d35400", // (NOx)
    "#7d3c98", // (PM)
    "#a93226", // (fallback)
  ];

  const [selectedState, setSelectedState] = useState("All States");
  const [mixedStyles, setMixedStyles] = useState([]);

  useEffect(() => {
    const fetchVesselState = async () => {
      if (!currentVessel?._id) {
        console.log("No vessel selected, skipping fetch");
        return;
      }

      if (!voyageId && (!startDate || !endDate)) {
        console.log(
          "No voyage selected and missing date range, skipping fetch"
        );
        return;
      }

      console.log("Fetching emission overview with:", {
        vesselId: currentVessel._id,
        voyageId,
        startDate: voyageId ? null : startDate,
        endDate: voyageId ? null : endDate,
      });

      setLoading(true);
      try {
        const response = await getVesselEmissionState(
          currentVessel._id,
          voyageId ? null : startDate,
          voyageId ? null : endDate,
          voyageId
        );

        if (response?.status === "error") {
          setError("Failed to fetch vessel state data");
          setVesselState(null);
        } else {
          setVesselState(response || []);
          setError(null);
        }
      } catch (err) {
        setError(err.message);
        console.error("API Error:", err);
        setVesselState(null);
      } finally {
        setLoading(false);
      }
    };

    fetchVesselState();
  }, [currentVessel, startDate, endDate, voyageId]);

  useEffect(() => {
    const createMixedStyles = () => {
      const getPattern = (i) => {
        if (!Highcharts.patterns || !Highcharts.patterns[i]) {
          return colors[i % colors.length];
        }
        return {
          pattern: Highcharts.merge(Highcharts.patterns[i], {
            color: colors[i % colors.length],
            backgroundColor: "#ffffff",
          }),
        };
      };

      const mixed = [colors[0], colors[1], colors[2], colors[3], colors[4]];
      setMixedStyles(mixed);
    };

    createMixedStyles();
  }, []);

  const preparePieData = () => {
    if (!vesselState || vesselState.length === 0) {
      return [];
    }

    const totalPercentage = vesselState.reduce(
      (sum, state) => sum + parseFloat(state.percentage || 0),
      0
    );

    if (selectedState === "All States") {
      return vesselState.map((state, index) => ({
        name: state.state,
        y: parseFloat(state.percentage) || 0,
        color:
          mixedStyles[index % mixedStyles.length] ||
          colors[index % colors.length],
        borderColor: "#ffffff",
        borderWidth: 1,
      }));
    } else {
      const selectedStateData = vesselState.find(
        (state) => state.state === selectedState
      );
      if (!selectedStateData) return [];

      return [
        {
          name: selectedState,
          y: parseFloat(selectedStateData.percentage) || 0,
          color:
            mixedStyles[
            vesselState.findIndex((state) => state.state === selectedState) %
            mixedStyles.length
            ] ||
            colors[
            vesselState.findIndex((state) => state.state === selectedState) %
            colors.length
            ],
          borderColor: "#ffffff",
          borderWidth: 1,
        },
        {
          name: "Other States",
          y: totalPercentage - (parseFloat(selectedStateData.percentage) || 0),
          color: "#f1f5f9",
          borderColor: "#e2e8f0",
          borderWidth: 1,
        },
      ];
    }
  };

  const pieData = preparePieData();

  const options = {
    chart: {
      type: "pie",
      height: 550,
      backgroundColor: "transparent",
    },
    title: {
      text: null,
    },
    tooltip: {
      pointFormat: "<b>{point.name}</b>: {point.y}%",
      backgroundColor: "#ffffff",
      borderColor: "#e2e8f0",
      borderRadius: 6,
      style: {
        color: "#1a202c",
        fontSize: "14px",
      },
    },
    plotOptions: {
      pie: {
        borderWidth: 1,
        borderColor: "#ffffff",
        allowPointSelect: true,
        cursor: "pointer",
        size: "70%", // Even smaller pie
        center: ["50%", "50%"],
        dataLabels: {
          enabled: true,
          format: "<b>{point.name}</b><br/>{point.percentage:.1f}%",
          distance: 25,
          softConnector: false,
          connectorWidth: 1,
          connectorColor: "#94a3b8",
          connectorPadding: 3,
          style: {
            color: "#1f2937",
            fontSize: "10px", // Smaller font
            fontWeight: "500",
            textOutline: "none",
          },
          useHTML: false,
          overflow: "allow",
          crop: false,
          padding: 1,
        },
        showInLegend: true,
      },
    },
    series: [
      {
        name: "Percentage",
        colorByPoint: true,
        data: pieData,
      },
    ],
    credits: {
      enabled: false,
    },
  };

  if (loading) {
    return (
      <div className="mx-auto">
        <h2 className="text-lg font-semibold text-gray-800 tracking-tight mb-4">
          Vessel State Transition
        </h2>
        <div className="bg-white/50 backdrop-blur border border-[#d7e9f7] p-8 shadow-[0_8px_20px_rgba(0,0,0,0.05)] rounded-lg text-center">
          Loading vessel state data...
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="mx-auto">
        <h2 className="text-lg font-semibold text-gray-800 tracking-tight mb-4">
          Vessel State Transition
        </h2>
        <div className="bg-white/50 backdrop-blur border border-[#d7e9f7] p-8 shadow-[0_8px_20px_rgba(0,0,0,0.05)] rounded-lg text-center text-red-500">
          {error}
        </div>
      </div>
    );
  }

  return (
    <div className="mx-auto">
      <div className="flex flex-col">
        <div className="px-6 py-4 border-b border-gray-100 bg-gray-50 flex justify-between items-center rounded-tl-2xl rounded-tr-2xl">
          <h2 className="text-lg font-semibold text-gray-800 tracking-tight">
            Vessel State Transition
          </h2>
        </div>

        {/* Added Filter Section */}
        {vesselState && vesselState.length > 0 && (
          <div className="flex justify-end gap-4 px-6 mt-4">
            <label className="text-base font-semibold text-gray-700 flex items-center gap-2">
              <SlidersHorizontal className="w-5 h-5 text-[#27ade3]" />
              Filter State:
            </label>
            <div className="relative">
              <select
                value={selectedState}
                onChange={(e) => setSelectedState(e.target.value)}
                className="w-full min-w-[180px] pl-4 pr-10 py-2.5 rounded-full bg-white border border-[#d7e9f7] text-gray-700 text-sm font-medium appearance-none hover:border-[#27ade3] focus:outline-none focus:ring-2 focus:ring-[#27ade3]/50 focus:border-transparent transition-all duration-200 shadow-sm"
              >
                <option value="All States" className="text-sm">
                  All States
                </option>
                {vesselState.map((state) => (
                  <option key={state.state} value={state.state} className="text-sm">
                    {state.state}
                  </option>
                ))}
              </select>
              <div className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none text-[#27ade3]">
                <ChevronDown className="w-4 h-4" />
              </div>
            </div>
          </div>
        )}
      </div>

      <div className="">
        {vesselState && vesselState.length > 0 ? (
          <HighchartsReact highcharts={Highcharts} options={options} />
        ) : (
          <div className="text-center py-8 text-gray-500">
            No vessel state data available
          </div>
        )}
      </div>
    </div>
  );
};

export default VesselStateTransition;